import { useState, useEffect } from "react";
import { Button, Select, Form, Input, Card, Spin, DatePicker, Switch, Modal } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEagleEyeAuth } from "hooks/useEagleEyeAuth";
import { useEagleEyeApi, Camera, CameraSearchParams, MediaStreamType, MediaType, EenFullFrameImageData, EenSearchVideoResponse } from "hooks/useEagleEyeApi";
import HlsVideoPlayer from "components/hls-video-player/hls-video-player";
import { formatEenTimestamp } from "utils/helpers";

const DEFAULT_TIME_RANGE: [Dayjs, Dayjs] = [
  dayjs().subtract(1, "day").startOf("day"),
  dayjs().endOf("day"),
];

type EagleEyeMethod = "getCameras" | "getCameraById" | "searchVideo";

const CAMERA_STATUS_OPTIONS = [
  { label: "Online", value: "online" },
  { label: "Device Offline", value: "deviceOffline" },
  { label: "Invalid Credentials", value: "invalidCredentials" },
];

const EagleEye: React.FC = () => {
  const { isAuthenticated, isAuthenticating, authError, login, logout } = useEagleEyeAuth();
  // When an EEN auth failure is detected, the hook calls clearAuthData() which sets
  // isAuthenticated → false, causing this component to render the login screen automatically.
  const {
    getCameras,
    getCameraById,
    listMedia,
    searchCameras,
    searchVideo,
    fetchVideoThumbnail,
    fetchVideoProxy,
    isLoading,
    error: apiError
  } = useEagleEyeApi();

  const [selectedMethod, setSelectedMethod] = useState<EagleEyeMethod>("getCameras");
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [cameraId, setCameraId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [videoTimeRange, setVideoTimeRange] = useState<[Dayjs, Dayjs]>(DEFAULT_TIME_RANGE);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [includeMedia, setIncludeMedia] = useState(false);
  const [mediaStreamType, setMediaStreamType] = useState<MediaStreamType>("main");
  const [mediaType, setMediaType] = useState<MediaType>("video");
  const [mediaStartDate, setMediaStartDate] = useState<Dayjs>(() => dayjs().subtract(1, "day"));
  const [thumbnailUrls, setThumbnailUrls] = useState<{ imgUrl: string; deeplink: string }[]>([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState<{ imgUrl: string; deeplink: string } | null>(null);
  const [totalResults, setTotalResults] = useState<number>(0);

  // Camera search form state
  const [searchParams, setSearchParams] = useState<CameraSearchParams>({});
  const [searchResponse, setSearchResponse] = useState<any>(null);

  // Revoke blob URLs when thumbnails are replaced or component unmounts
  useEffect(() => {
    return () => {
      thumbnailUrls.forEach(({ imgUrl }) => {
        if (imgUrl.startsWith("blob:")) URL.revokeObjectURL(imgUrl);
      });
    };
  }, [thumbnailUrls]);

  const handleCameraSelect = async (camera: Camera) => {
    setSelectedMethod("getCameraById");
    setCameraId(camera.id);
    setApiResponse(null);
    setThumbnailUrls([]);
    setTotalResults(0);

    try {
      const response = await getCameraById(camera.id);
      setApiResponse(response);
    } catch (err) {
      console.error("Error fetching camera by ID:", err);
    }
  };

  const handleCameraSearch = async () => {
    setSearchResponse(null);
    const result = await searchCameras(searchParams);
    setSearchResponse(result);
  };

  const handleExecuteMethod = async () => {
    setApiResponse(null);
    setThumbnailUrls([]);
    setTotalResults(0);

    try {
      let response;
      switch (selectedMethod) {
        case "getCameras":
          response = await getCameras();
          if (response?.results) {
            setCameras(response.results);
          }
          break;
        case "getCameraById":
          if (!cameraId.trim()) {
            return;
          }
          response = await getCameraById(cameraId);
          if (response && includeMedia) {
            const mediaResponse = await listMedia({
              deviceId: cameraId,
              type: mediaStreamType,
              mediaType,
              startTimestampGte: mediaStartDate.toISOString().replace("Z", "+00:00"),
              mostRecentOnly: true,
            });
            response = { ...response, media: mediaResponse?.results ?? [] };
          }
          break;
        case "searchVideo":
          if (!searchQuery.trim()) {
            alert("Please enter a search query");
            return;
          }
          response = await searchVideo(searchQuery, undefined, {
            start: videoTimeRange[0].toISOString().replace("Z", "+00:00"),
            end: videoTimeRange[1].toISOString().replace("Z", "+00:00"),
          }) as EenSearchVideoResponse | null;

          if (response) {
            const results = response.results ?? [];
            setTotalResults(results.length);
            const candidates = results
              .slice(0, 10)
              .map((result: EenSearchVideoResponse["results"][number]) => {
                const imageData = result.data?.find(
                  (d: EenSearchVideoResponse["results"][number]["data"][number]): d is EenFullFrameImageData =>
                    d.type === "een.fullFrameImageUrl.v1"
                );
                if (!imageData) return null;
                const esn = result.actorType === "camera" ? result.actorId : undefined;
                const deeplink = esn
                  ? `https://webapp.eagleeyenetworks.com/#/history?ids=${esn}&time=${formatEenTimestamp(imageData.timestamp)}`
                  : undefined;
                return { httpsUrl: imageData.httpsUrl, deeplink };
              })
              .filter((item): item is { httpsUrl: string; deeplink: string | undefined } => item !== null);

            const thumbnails = await Promise.all(
              candidates.map(async ({ httpsUrl, deeplink }) => ({
                imgUrl: await fetchVideoThumbnail(httpsUrl),
                deeplink,
              }))
            );
            setThumbnailUrls(thumbnails as { imgUrl: string; deeplink: string }[]);
          }
          break;
        default:
          throw new Error("Unknown method");
      }
      setApiResponse(response);
    } catch (err) {
      console.error("Error executing method:", err);
    }
  };

  // Show loading state during authentication
  if (isAuthenticating) {
    return (
      <div className="ee-container">
        <span className="page-title">Eagle Eye Network</span>
        <div className="ee-content-container">
          <Card className="ee-loading-card">
            <Spin size="large" />
            <h2>Authenticating with Eagle Eye Network...</h2>
          </Card>
        </div>
      </div>
    );
  }

  // Show authentication error
  if (authError) {
    return (
      <div className="ee-container">
        <span className="page-title">Eagle Eye Network</span>
        <div className="ee-content-container">
          <Card className="ee-error-card">
            <h2>Authentication Error</h2>
            <p>{authError}</p>
            <Button className="ee-retry-button" onClick={login}>
              Try Again
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Show login button if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="ee-container">
        <span className="page-title">Eagle Eye Network</span>
        <div className="ee-content-container">
          <Card className="ee-login-card">
            <h2>Eagle Eye Networks Integration</h2>
            <p className="ee-description">
              Please authenticate to access Eagle Eye Network API
            </p>
            <Button className="ee-login-button" onClick={login}>
              Login with Eagle Eye Networks
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Main authenticated UI
  return (
    <div className="ee-container">
      <span className="page-title">Eagle Eye Network</span>

      <div className="ee-content-container">
        <div className="ee-logout-section">
          <Button className="ee-logout-button" onClick={logout}>
            Logout
          </Button>
        </div>

        <Form className="ee-form">
          <span className="ee-form-title">API Tester</span>
          <p className="ee-description">
            Test Eagle Eye Network API endpoints
          </p>

          <Form.Item className="ee-form-item" required>
            <label className="ee-label">Select Method</label>
            <Select
              value={selectedMethod}
              className="ee-select"
              onChange={(value) => {
                setSelectedMethod(value as EagleEyeMethod);
                setApiResponse(null);
                setCameras([]);
                setThumbnailUrls([]);
                setTotalResults(0);
              }}
              suffixIcon={
                <img
                  src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
                  alt="dropdown icon"
                  className="ee-chevron-button"
                />
              }
            >
              <Select.Option value="getCameras">Get All Cameras</Select.Option>
              <Select.Option value="getCameraById">Get Camera by ID</Select.Option>
              <Select.Option value="searchVideo">Search Video (Natural Language)</Select.Option>
            </Select>
          </Form.Item>

          {selectedMethod === "getCameraById" && (
            <>
              <Form.Item className="ee-form-item" required>
                <label className="ee-label">Camera ID</label>
                <Input
                  className="ee-input"
                  value={cameraId}
                  onChange={(e) => setCameraId(e.target.value)}
                  placeholder="Enter camera ID"
                />
              </Form.Item>
              <Form.Item className="ee-form-item">
                <label className="ee-label">Include Most Recent Media</label>
                <Switch checked={includeMedia} onChange={setIncludeMedia} />
              </Form.Item>
              {includeMedia && (
                <>
                  <Form.Item className="ee-form-item" required>
                    <label className="ee-label">Stream Type</label>
                    <Select
                      className="ee-select"
                      value={mediaStreamType}
                      onChange={(v) => setMediaStreamType(v as MediaStreamType)}
                      options={[
                        { label: "Main", value: "main" },
                        { label: "Preview", value: "preview" },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item className="ee-form-item" required>
                    <label className="ee-label">Media Type</label>
                    <Select
                      className="ee-select"
                      value={mediaType}
                      onChange={(v) => setMediaType(v as MediaType)}
                      options={[
                        { label: "Video", value: "video" },
                        { label: "Image", value: "image" },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item className="ee-form-item" required>
                    <label className="ee-label">Search Media From</label>
                    <DatePicker
                      className="ee-input"
                      showTime
                      value={mediaStartDate}
                      onChange={(v) => { if (v) setMediaStartDate(v); }}
                    />
                  </Form.Item>
                </>
              )}
            </>
          )}

          {selectedMethod === "searchVideo" && (
            <>
              <Form.Item className="ee-form-item" required>
                <label className="ee-label">Search Query</label>
                <Input
                  className="ee-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='e.g., "blue truck at entry gate in past 7 days"'
                />
              </Form.Item>
              <Form.Item className="ee-form-item" required>
                <label className="ee-label">Time Range</label>
                <DatePicker.RangePicker
                  className="ee-input"
                  showTime
                  value={videoTimeRange}
                  onChange={(values) => {
                    if (values?.[0] && values?.[1]) {
                      setVideoTimeRange([values[0], values[1]]);
                    }
                  }}
                />
              </Form.Item>
            </>
          )}

          <div className="ee-button-container">
            <Button
              className="ee-execute-button"
              onClick={handleExecuteMethod}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Execute'}
            </Button>
          </div>
        </Form>

        {/* <Form className="ee-form">
          <span className="ee-form-title">Camera Search</span>
          <p className="ee-description">
            Filter cameras using the Eagle Eye list cameras API
          </p>

          <Form.Item className="ee-form-item">
            <label className="ee-label">Text Search</label>
            <Input
              className="ee-input"
              value={searchParams.q ?? ""}
              onChange={(e) => setSearchParams((p) => ({ ...p, q: e.target.value || undefined }))}
              placeholder="Search name, ID, tags, location…"
            />
          </Form.Item>

          <Form.Item className="ee-form-item">
            <label className="ee-label">Name Contains</label>
            <Input
              className="ee-input"
              value={searchParams.name__contains ?? ""}
              onChange={(e) => setSearchParams((p) => ({ ...p, name__contains: e.target.value || undefined }))}
              placeholder="Partial camera name"
            />
          </Form.Item>

          <Form.Item className="ee-form-item">
            <label className="ee-label">Status</label>
            <Select
              mode="multiple"
              className="ee-select"
              value={searchParams.status__in ?? []}
              onChange={(values) => setSearchParams((p) => ({ ...p, status__in: values.length ? values : undefined }))}
              options={CAMERA_STATUS_OPTIONS}
              placeholder="Any status"
            />
          </Form.Item>

          <Form.Item className="ee-form-item">
            <label className="ee-label">Location IDs (comma-separated)</label>
            <Input
              className="ee-input"
              value={searchParams.locationId__in?.join(",") ?? ""}
              onChange={(e) => {
                const ids = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                setSearchParams((p) => ({ ...p, locationId__in: ids.length ? ids : undefined }));
              }}
              placeholder="e.g. loc1,loc2"
            />
          </Form.Item>

          <Form.Item className="ee-form-item">
            <label className="ee-label">Tags (comma-separated)</label>
            <Input
              className="ee-input"
              value={searchParams.tags__any?.join(",") ?? ""}
              onChange={(e) => {
                const tags = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                setSearchParams((p) => ({ ...p, tags__any: tags.length ? tags : undefined }));
              }}
              placeholder="e.g. entrance,parking"
            />
          </Form.Item>

          <div className="ee-button-container">
            <Button
              className="ee-execute-button"
              onClick={handleCameraSearch}
              disabled={isLoading}
            >
              {isLoading ? "Loading…" : "Search Cameras"}
            </Button>
          </div>
        </Form> */}

        {cameras.length > 0 && (
          <Card className="ee-response-card">
            <div className="ee-response-header">
              <h3>Cameras ({cameras.length})</h3>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {cameras.map((camera) => (
                <Button
                  key={camera.id}
                  className="ee-execute-button"
                  onClick={() => handleCameraSelect(camera)}
                >
                  {camera.name || camera.id}
                </Button>
              ))}
            </div>
          </Card>
        )}

        {searchResponse && (
          <Card className="ee-response-card">
            <div className="ee-response-header">
              <h3>Camera Search Results</h3>
            </div>
            <pre className="ee-response-content">
              {JSON.stringify(searchResponse, null, 2)}
            </pre>
          </Card>
        )}

        {apiError && (
          <Card className="ee-error-card">
            <h3>Error</h3>
            <p>{apiError}</p>
          </Card>
        )}

        {apiResponse && (
          <Card className="ee-response-card">
            <div className="ee-response-header">
              <h3>Response</h3>
            </div>
            {thumbnailUrls.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p>Showing first {thumbnailUrls.length} images of {totalResults} results</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {thumbnailUrls.map(({ imgUrl, deeplink }, i) => (
                    <img
                      key={imgUrl}
                      src={imgUrl}
                      alt={`Video search result ${i + 1}`}
                      style={{ width: 200, height: 120, objectFit: "cover", cursor: "pointer", display: "block" }}
                      onClick={() => setSelectedThumbnail({ imgUrl, deeplink })}
                    />
                  ))}
                </div>

                <Modal
                  className="ee-img-preview-modal"
                  open={selectedThumbnail !== null}
                  onCancel={() => setSelectedThumbnail(null)}
                  footer={null}
                  centered
                >
                  {selectedThumbnail && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, paddingTop: 8 }}>
                      <img
                        src={selectedThumbnail.imgUrl}
                        alt="Video search result"
                        style={{ width: "100%", objectFit: "contain" }}
                      />
                      {selectedThumbnail.deeplink && (
                        <a
                          type="button"
                          className="highnote-button"
                          href={selectedThumbnail.deeplink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View History
                        </a>
                      )}
                    </div>
                  )}
                </Modal>
              </div>
            )}
            <pre className="ee-response-content">
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          </Card>
        )}

        {apiResponse?.media?.length > 0 && (
          <Card className="ee-response-card">
            <div className="ee-response-header">
              <h3>Media</h3>
            </div>
            {apiResponse.media.map((item: any, i: number) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <p style={{ margin: "0 0 4px" }}>
                  <strong>Start:</strong> {item.startTimestamp}
                </p>
                <p style={{ margin: "0 0 8px" }}>
                  <strong>End:</strong> {item.endTimestamp}
                </p>
                {item.hlsUrl && (() => {
                  const { src } = fetchVideoProxy(item.hlsUrl);
                  return <HlsVideoPlayer src={src} />;
                })()}
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
};

export default EagleEye;
